// src/features/learnerImpact/tutorFeedbackOptions.js
// Shared definitions for tutor feedback (version 2, from Oct 2026).
// Used by the tutor's lesson screen (to ask) and by admin reports (to show).
// If the wording of a question changes after the pilot, raise FEEDBACK_VERSION.
//
// Where each question is asked (one lesson = one tutor session):
//   per unit, per learner    → participation + preparation (or Not present)
//   per session, per learner → overall (⭐ / 👍 / 🔄), on "Closing the session"

export const FEEDBACK_VERSION = 2;

// Firestore collection for the per-session Overall ratings
export const SESSION_RATINGS_COLLECTION = 'tutor_session_ratings';

export const PARTICIPATION_QUESTION = {
  key: 'participation',
  label: 'Participation',
  question: 'How actively did the learner participate, ask or answer questions, and show interest?',
};

export const PREPARATION_QUESTION = {
  key: 'preparation',
  label: 'Preparation',
  question: 'Was the learner ready for the session, familiar with the material?',
};

// Asked per unit, in this order
export const UNIT_QUESTIONS = [PARTICIPATION_QUESTION, PREPARATION_QUESTION];

// Options for Participation and Preparation.
// "Not observed" has no score and is left out of averages.
export const RATING_OPTIONS = [
  { value: 'weak', label: 'Weak', score: 1 },
  { value: 'good', label: 'Good', score: 2 },
  { value: 'excellent', label: 'Excellent', score: 3 },
  { value: 'not_observed', label: 'Not observed', score: null },
];

export const RATING_BY_VALUE = Object.fromEntries(RATING_OPTIONS.map(o => [o.value, o]));

// Overall — asked once per learner when the session is closed.
// "Revisit" = visual cue for the admin that the learner needs help.
export const OVERALL_QUESTION = {
  key: 'overall',
  label: 'Overall',
  question: 'How was the session for this learner overall?',
};

export const OVERALL_OPTIONS = {
  great: { icon: '⭐', label: 'Great', tutorLabel: 'Great session!' },
  good: { icon: '👍', label: 'Good', tutorLabel: "Good, let's go deeper next time" },
  revisit: { icon: '🔄', label: 'Revisit', tutorLabel: 'Needs to revisit — help needed' },
};

// Old records have no feedbackVersion → version 1 (overall per unit only).
export const getFeedbackVersion = (record) => record?.feedbackVersion || 1;

// Counts each option and the average score for a list of rating values.
// Missing values are ignored; "Not observed" is counted, not scored.
export const summarizeRatings = (values) => {
  const counts = Object.fromEntries(RATING_OPTIONS.map(o => [o.value, 0]));
  const scores = [];
  let answered = 0;
  values.forEach(v => {
    const option = RATING_BY_VALUE[v];
    if (!option) return;
    answered++;
    counts[option.value]++;
    if (option.score != null) scores.push(option.score);
  });
  const average = scores.length
    ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
    : null;
  return { counts, average, answered };
};